import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import restaurantConfig from '../config/restaurantConfig';
import { OpeningHours, TimeSlot } from '../types';
import { useMerchant } from '../context/MerchantContext';
import { formatPhone } from '../utils/formatters';


const FooterContainer = styled.footer`
  background-color: rgba(0, 0, 0, 0.05);
  color: ${props => props.theme.text || '#1A202C'};
  padding: 3rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;


const FooterTitle = styled.h3<{ themeColor?: string }>`
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${props => props.themeColor || '#e74c3c'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FooterLink = styled(Link)<{ themeColor?: string }>`
  color: ${props => props.theme.text || '#1A202C'};
  text-decoration: none;
  margin-bottom: 0.5rem;
  opacity: 0.8;

  &:hover {
    color: ${props => props.themeColor || '#e74c3c'};
    opacity: 1;
  }
`;

const FooterText = styled.p`
  color: ${props => props.theme.text || '#1A202C'};
  margin-bottom: 0.5rem;
  opacity: 0.8;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a<{ themeColor?: string }>`
  color: ${props => props.theme.text || '#1A202C'};
  font-size: 1.5rem;

  &:hover {
    color: ${props => props.themeColor || '#e74c3c'};
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  color: ${props => props.theme.lightText || '#4A5568'};
  font-size: 0.9rem;
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { restaurant } = useMerchant();

  const themeColor = restaurant?.theme?.primary;
  const name = restaurant?.name || restaurantConfig.restaurant.name;
  const description = restaurant?.description || restaurantConfig.restaurant.description;
  const address = restaurant?.address;
  const contact = restaurant?.contact;
  const openingHours = restaurant?.openingHours || restaurantConfig.restaurant.openingHours;

  return (
    <FooterContainer>
      <div className="container">
        <div className="row">
          <div className="col-12 col-md-6 col-lg-3 mb-4 mb-lg-0 text-center text-md-start">
            <FooterTitle themeColor={themeColor}>{name}</FooterTitle>
            <FooterText dangerouslySetInnerHTML={{ __html: description || 'Nenhuma descrição disponível.' }} />
            <SocialLinks className="justify-content-center justify-content-md-start">
              {contact?.socialMedia?.facebook && (
                <SocialLink themeColor={themeColor} href={contact.socialMedia.facebook} target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-facebook"></i>
                </SocialLink>
              )}
              {contact?.socialMedia?.instagram && (
                <SocialLink themeColor={themeColor} href={contact.socialMedia.instagram} target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-instagram"></i>
                </SocialLink>
              )}
              {contact?.socialMedia?.twitter && (
                <SocialLink themeColor={themeColor} href={contact.socialMedia.twitter} target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-twitter"></i>
                </SocialLink>
              )}
              {contact?.socialMedia?.whatsapp && (
                <SocialLink themeColor={themeColor} href={contact.socialMedia.whatsapp} target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-whatsapp"></i>
                </SocialLink>
              )}
            </SocialLinks>
          </div>

          <div className="col-12 col-md-6 col-lg-3 mb-4 mb-lg-0 text-center text-md-start">
            <FooterTitle themeColor={themeColor}>Links Rápidos</FooterTitle>
            <div className="d-flex flex-column">
              <FooterLink themeColor={themeColor} to="/">Home</FooterLink>
              <FooterLink themeColor={themeColor} to="/menu">Cardápio</FooterLink>
              <FooterLink themeColor={themeColor} to="/about">Sobre</FooterLink>
              <FooterLink themeColor={themeColor} to="/contact">Contato</FooterLink>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-3 mb-4 mb-lg-0 text-center text-md-start">
            <FooterTitle themeColor={themeColor}>Contato</FooterTitle>
            {address && (
              <FooterText>
                <i className="fas fa-map-marker-alt me-2"></i> {address.street}, {address.number} - {address.neighborhood}, {address.city}, {address.state}
              </FooterText>
            )}
            {contact?.phone && (
              <FooterText>
                <i className="fas fa-phone me-2"></i> {formatPhone(contact.phone)}
              </FooterText>
            )}
            {contact?.email && (
              <FooterText>
                <i className="fas fa-envelope me-2"></i> {contact.email}
              </FooterText>
            )}
            <FooterText>
              <i className="fas fa-globe me-2"></i> {window.location.hostname}
            </FooterText>
          </div>

          <div className="col-12 col-md-6 col-lg-3 text-center text-md-start">
            <FooterTitle themeColor={themeColor}>Horário de Funcionamento</FooterTitle>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => {
              const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
              const dayHours = openingHours.find(oh => oh.dayOfWeek === day);
              
              return (
                <div key={day} className="mb-1">
                  <FooterText className="mb-0" style={{ fontSize: '0.85rem' }}>
                    <strong>{dayNames[day]}:</strong> {dayHours && dayHours.timeSlots && dayHours.timeSlots.length > 0 
                      ? dayHours.timeSlots.map(slot => `${slot.open} - ${slot.close}`).join(' / ') 
                      : 'Fechado'}
                  </FooterText>
                </div>
              );
            })}
          </div>
        </div>

        <Copyright>
          &copy; {currentYear} {name}. Todos os direitos reservados.
        </Copyright>
      </div>
    </FooterContainer>
  );
};

export default Footer;
